import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Keyboard, Check, ShieldCheck, Sparkles, Lock, RefreshCw, ExternalLink } from "lucide-react";
import { api } from "@/lib/api/client";
import { environment } from "@/config/environment";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  armBCKeyboard,
  readBCKeyboardState,
  disarmBCKeyboard,
  isKeyboardCapable,
  isKeyboardArmed,
} from "@/lib/keyboard/bridge";

// The council goes where you type. This screen mints a dedicated bc_live_* key,
// hands it (plus plan + preferred brain) to the native keyboard extension via
// Capacitor Preferences, and walks the user through enabling it in iOS Settings.
const BCKeyboardSetup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const native = isKeyboardCapable();
  const [planId, setPlanId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [armed, setArmed] = useState(false);
  const [brain, setBrain] = useState("");
  const [arming, setArming] = useState(false);
  const [testing, setTesting] = useState(false);

  const isPaid = !!planId && planId !== "free";

  useEffect(() => {
    (async () => {
      try {
        const state = await readBCKeyboardState();
        setArmed(isKeyboardArmed(state));
        if (state.brain) setBrain(state.brain);
      } catch {
        /* non-native / no prefs yet — fine */
      }
      try {
        const sub = await api.get<{ planId?: string; plan?: string }>("/api/subscription/status", {});
        setPlanId(sub?.planId ?? sub?.plan ?? "free");
      } catch {
        setPlanId("free");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Mint a fresh, independently-revocable key scoped to the keyboard.
  const mintKey = async (): Promise<string> => {
    const res: any = await api.post("/api/keys", { label: "BC Keyboard (iOS)" });
    const secret = res?.secret ?? res?.key ?? res?.apiKey ?? res?.data?.secret;
    if (!secret) throw new Error("Key service returned no secret");
    return secret as string;
  };

  const arm = async (opts: { regenerate?: boolean } = {}) => {
    if (!native) {
      toast({ title: "Open the BrainColonies iOS app", description: "The keyboard sets up from inside the app on your iPhone." });
      return;
    }
    if (!isPaid) {
      navigate("/settings/subscription");
      return;
    }
    setArming(true);
    try {
      const existing = await readBCKeyboardState();
      const apiKey = !opts.regenerate && existing.apiKey ? existing.apiKey : await mintKey();
      await armBCKeyboard({
        apiKey,
        planId,
        brain: brain.trim(),
        apiBase: environment.apiBaseUrl,
      });
      setArmed(true);
      toast({ title: "Keyboard armed", description: "Your council now travels with you. Enable it in iOS Settings below." });
    } catch (err: any) {
      toast({ title: "Couldn't arm the keyboard", description: err?.message ?? "unknown error", variant: "destructive" });
    } finally {
      setArming(false);
    }
  };

  // Best-effort end-to-end proof: the stored key can actually reach the council.
  const test = async () => {
    setTesting(true);
    try {
      const state = await readBCKeyboardState();
      if (!state.apiKey) throw new Error("Arm the keyboard first");
      const res = await fetch(`${environment.apiBaseUrl.replace(/\/$/, "")}/mcp`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${state.apiKey}` },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: 1,
          method: "tools/call",
          params: { name: "bc.checkIn", arguments: { userMessage: "keyboard setup test" } },
        }),
      });
      if (!res.ok) throw new Error(`Council returned HTTP ${res.status}`);
      toast({ title: "Council reachable ✓", description: "The keyboard's key works end-to-end." });
    } catch (err: any) {
      toast({ title: "Test failed", description: err?.message ?? "unknown error", variant: "destructive" });
    } finally {
      setTesting(false);
    }
  };

  const disarm = async () => {
    await disarmBCKeyboard();
    setArmed(false);
    toast({ title: "Keyboard disarmed", description: "It will type normally but stay quiet until re-armed." });
  };

  return (
    <div className="min-h-screen bg-background safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-ios-separator bg-ios-surface">
        <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} className="text-foreground w-11 h-11 rounded-full">
          <ChevronLeft size={22} />
        </Button>
        <h1 className="ios-title text-foreground">BC Keyboard</h1>
        <div className="w-11" />
      </div>

      <div className="max-w-2xl mx-auto px-5 py-6 space-y-5">
        {/* Hero */}
        <div className="rounded-2xl border border-ios-separator bg-ios-surface p-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
              <Keyboard size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-foreground">Your council, wherever you type</p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                A business email, a WhatsApp reply, any app — summon alt-me or a brain to Improve, Soften, or Tighten
                what you're writing. Suggestions only; you Accept or Dismiss. Part of Lite and above.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground px-1">Loading…</p>
        ) : !native ? (
          <div className="rounded-2xl border border-ios-separator bg-ios-surface p-5 flex items-start gap-3">
            <ExternalLink size={18} className="text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              Open <span className="text-foreground font-medium">BrainColonies</span> on your iPhone to set up the keyboard —
              this screen arms the native extension, which only exists in the iOS app.
            </p>
          </div>
        ) : !isPaid ? (
          <div className="rounded-2xl border border-ios-separator bg-ios-surface p-5">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-foreground/10 flex items-center justify-center shrink-0">
                <Lock size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">The keyboard is part of Lite and above</p>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                  Your plan is {planId === "free" ? "Free" : planId}. Upgrade to carry your council into every app on your phone.
                </p>
                <Button className="mt-4" onClick={() => navigate("/settings/subscription")}>See plans</Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Step 1 — arm */}
            <div className="rounded-2xl border border-ios-separator bg-ios-surface p-5">
              <StepHead n={1} title="Arm the keyboard" done={armed} />
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Creates a dedicated key (revocable anytime in Developers) and hands it to the keyboard with your plan.
              </p>
              <div className="mt-4">
                <label className="text-xs font-medium text-muted-foreground">Preferred voice (optional)</label>
                <input
                  value={brain}
                  onChange={(e) => setBrain(e.target.value)}
                  placeholder="alt-me (leave blank), or a brain's name"
                  className="mt-1.5 w-full rounded-lg bg-background border border-ios-separator px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary"
                />
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {!armed ? (
                  <Button onClick={() => arm()} disabled={arming} className="gap-2">
                    <Sparkles size={15} /> {arming ? "Arming…" : "Arm keyboard"}
                  </Button>
                ) : (
                  <>
                    <Button onClick={() => arm()} disabled={arming} variant="outline" className="gap-2">
                      <Check size={15} /> {arming ? "Saving…" : "Save voice"}
                    </Button>
                    <Button onClick={test} disabled={testing} variant="outline" className="gap-2">
                      <ShieldCheck size={15} /> {testing ? "Testing…" : "Test council"}
                    </Button>
                    <Button onClick={() => arm({ regenerate: true })} disabled={arming} variant="ghost" className="gap-2 text-muted-foreground">
                      <RefreshCw size={14} /> Regenerate key
                    </Button>
                  </>
                )}
              </div>
              {armed && (
                <button onClick={disarm} className="text-xs text-muted-foreground hover:text-destructive mt-3 transition-colors">
                  Disarm keyboard
                </button>
              )}
            </div>

            {/* Step 2 — add keyboard */}
            <div className="rounded-2xl border border-ios-separator bg-ios-surface p-5">
              <StepHead n={2} title="Add the keyboard in iOS Settings" />
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Settings → General → Keyboard → Keyboards → <span className="text-foreground">Add New Keyboard…</span> → BrainColonies.
              </p>
            </div>

            {/* Step 3 — full access */}
            <div className="rounded-2xl border border-ios-separator bg-ios-surface p-5">
              <StepHead n={3} title="Allow Full Access" />
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Tap BrainColonies in that keyboards list and turn on <span className="text-foreground">Allow Full Access</span>.
                It's what lets the keyboard reach your council over the network — nothing you type is stored, and it stays
                silent until you tap a suggestion.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StepHead = ({ n, title, done }: { n: number; title: string; done?: boolean }) => (
  <div className="flex items-center gap-2.5">
    <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${done ? "bg-primary text-primary-foreground" : "bg-foreground/10 text-foreground"}`}>
      {done ? <Check size={13} /> : n}
    </div>
    <p className="text-sm font-semibold text-foreground">{title}</p>
  </div>
);

export default BCKeyboardSetup;
